import { useMemo, type ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { differenceInCalendarDays, format, formatDistanceToNowStrict } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Handshake } from "lucide-react";
import { DataTable } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import type { Lead } from "@/hooks/useLeads";
import { barterBadgeLabel } from "@/lib/barter";
import { formatBRL } from "@/lib/format";
import { leadSourceLabel } from "@/lib/leadSource";
import { STAGE_META, STAGE_ORDER, type LeadDeal, type PipelineStage } from "@/lib/pipelineStage";
import { cn } from "@/lib/utils";
import { LeadActionsMenu, type LeadActions } from "./LeadActionsMenu";

/**
 * Leads & propostas em modo tabela (`?view=table`). Mesmo recorte do funil
 * (busca + filtro de estágio já aplicados pelo pai); ordena localmente por
 * cabeçalho. Abrir o lead é o botão do nome — a linha não é clicável
 * (DataTable). Regras: flows/lead-intake.md R10.
 */
export interface LeadsTableProps {
  leads: Lead[];
  stageOf: (leadId: string) => PipelineStage;
  deals: Record<string, LeadDeal>;
  onOpen: (lead: Lead, stage: PipelineStage) => void;
  actionsFor: (lead: Lead, stage: PipelineStage) => LeadActions;
  isLoading?: boolean;
  /** Renderizado no lugar das linhas quando a query de leads falhou. */
  error?: ReactNode;
}

const time = (iso: string | null) => (iso ? new Date(iso).getTime() : 0);

export function LeadsTable({
  leads,
  stageOf,
  deals,
  onOpen,
  actionsFor,
  isLoading,
  error,
}: LeadsTableProps) {
  const columns = useMemo<ColumnDef<Lead, unknown>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Nome",
        meta: { minWidth: 220 },
        cell: ({ row }) => {
          const lead = row.original;
          return (
            <div className="min-w-0 max-w-[320px]">
              <button
                type="button"
                onClick={() => onOpen(lead, stageOf(lead.id))}
                className="block max-w-full truncate rounded-sm text-left font-semibold hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {lead.name}
              </button>
              {lead.project_type && (
                <p className="truncate text-[11.5px] text-muted-foreground">{lead.project_type}</p>
              )}
            </div>
          );
        },
      },
      {
        id: "contact",
        accessorFn: (l) => l.email ?? l.phone ?? "",
        header: "Contato",
        meta: { minWidth: 180 },
        cell: ({ row }) => {
          const { email, phone } = row.original;
          if (!email && !phone) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="min-w-0 text-[12.5px] leading-[1.45]">
              {email && <p className="max-w-[240px] truncate">{email}</p>}
              {phone && <p className="text-muted-foreground">{phone}</p>}
            </div>
          );
        },
      },
      {
        id: "stage",
        accessorFn: (l) => STAGE_ORDER[stageOf(l.id)],
        header: "Estágio",
        cell: ({ row }) => {
          const meta = STAGE_META[stageOf(row.original.id)];
          return (
            <span
              className={cn(
                "inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[11px] font-semibold",
                meta.pill,
              )}
            >
              <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} />
              {meta.label}
            </span>
          );
        },
      },
      {
        id: "value",
        accessorFn: (l) => deals[l.id]?.value ?? 0,
        header: "Valor",
        meta: { align: "right" },
        cell: ({ row }) => {
          const deal = deals[row.original.id];
          const barter = barterBadgeLabel(deal?.barterPercent ?? 0);
          return (
            <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap font-semibold">
              {deal?.value ? formatBRL(deal.value, { compact: true }) : "—"}
              {barter && (
                <span
                  className="inline-flex items-center gap-0.5 rounded-[6px] border border-border px-1.5 py-px text-[10.5px] font-semibold text-muted-foreground"
                  title="Parte do valor liquidada em serviços"
                >
                  <Handshake className="h-3 w-3" />
                  {barter}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: "source",
        accessorFn: (l) => leadSourceLabel(l.source),
        header: "Origem",
        cell: ({ getValue }) => (
          <span className="whitespace-nowrap text-muted-foreground">{getValue<string>()}</span>
        ),
      },
      {
        id: "next",
        // Sem follow-up vai para o fim na ordem crescente.
        accessorFn: (l) =>
          l.next_follow_up_at ? time(l.next_follow_up_at) : Number.MAX_SAFE_INTEGER,
        header: "Próximo passo",
        meta: { minWidth: 200 },
        cell: ({ row }) => <NextStepCell lead={row.original} stage={stageOf(row.original.id)} />,
      },
      {
        id: "updated",
        accessorFn: (l) => time(l.updated_at),
        header: "Atualizado",
        cell: ({ row }) => (
          <time
            dateTime={row.original.updated_at}
            title={format(new Date(row.original.updated_at), "dd/MM/yyyy HH:mm")}
            className="whitespace-nowrap text-muted-foreground"
          >
            {formatDistanceToNowStrict(new Date(row.original.updated_at), {
              addSuffix: true,
              locale: ptBR,
            })}
          </time>
        ),
      },
      {
        id: "actions",
        enableSorting: false,
        header: () => <span className="sr-only">Ações</span>,
        meta: { label: "Ações", align: "right" },
        cell: ({ row }) => (
          <LeadActionsMenu
            leadName={row.original.name}
            {...actionsFor(row.original, stageOf(row.original.id))}
          />
        ),
      },
    ],
    [stageOf, deals, onOpen, actionsFor],
  );

  return (
    <DataTable
      ariaLabel="Leads e propostas"
      columns={columns}
      data={leads}
      getRowId={(l) => l.id}
      isLoading={isLoading}
      error={error}
      stickyFirstColumn
      minWidth={1040}
      empty={<EmptyState size="sm" title="Nenhum lead." className="py-8" />}
      footer={
        <p className="px-5 py-3 text-[12px] text-muted-foreground">
          {leads.length} {leads.length === 1 ? "lead" : "leads"}
        </p>
      }
    />
  );
}

function NextStepCell({ lead, stage }: { lead: Lead; stage: PipelineStage }) {
  if (stage === "lost") {
    return lead.lost_reason ? (
      <p className="line-clamp-2 text-[12px] text-bad">Motivo: {lead.lost_reason}</p>
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }
  const due = lead.next_follow_up_at ? new Date(lead.next_follow_up_at) : null;
  const overdue = due ? differenceInCalendarDays(due, new Date()) < 0 : false;
  if (!lead.next_step && !due) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="min-w-0 max-w-[280px] text-[12.5px] leading-[1.45]">
      {lead.next_step && <p className="truncate">{lead.next_step}</p>}
      {due && (
        <p
          className={cn(
            "text-[11.5px]",
            overdue ? "font-semibold text-bad" : "text-muted-foreground",
          )}
        >
          {overdue ? "Atrasado · " : "Follow-up "}
          {format(due, "dd/MM", { locale: ptBR })}
        </p>
      )}
    </div>
  );
}
